import type {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormFieldOptions,
  FormResponseValue,
} from "@tellescope/types-models";
import { useCallback, useMemo } from "react";
import { SupportedSession, TellescopeModelFromName } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { convertFormFieldType } from "../utils/convert-form-field-type";
import { responsesSatisfyConditions } from "../utils";
import type { NextFieldLogicOptions } from "../utils/types";
import type { FormTypesWithValues, TellescopeFieldResponse } from "../types";
import { TellescopeFormField } from "../tellescope-form-field";
import { FieldDescription } from "../FieldDescription";
import { cn } from "@tellescope/ui";

export interface QuestionGroupFieldProps {
  id: string;
  title?: string;
  options?: FormFieldOptions;
  className?: string;
  allFields: TellescopeModelFromName<"form_fields">[];
  currentResponses: Record<string, TellescopeFieldResponse>;
  getFieldProps: <T extends FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    description?: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T]) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
  } | null;
  logicOptions: NextFieldLogicOptions;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  session?: SupportedSession | null;
  form?: TellescopeModelFromName<"forms">;
  formResponses?: FormResponseValue[];
  enduserId?: string;
  organizationSubdomain?: string;
  description?: string;
  htmlDescription?: string;
  applyDatabasePrepopulation?: (records: DatabaseRecord[]) => void;
  setCustomerId?: (customerId: string) => void;
}

/**
 * Renders a Question Group field — a container that displays multiple sub-fields
 * together as a single logical unit. Sub-fields are rendered inline and each
 * manages its own value/response independently.
 *
 * Conditional visibility of individual sub-fields is supported via `groupShowCondition`.
 */
export const QuestionGroupField = ({
  title,
  options,
  className,
  allFields,
  currentResponses,
  getFieldProps,
  logicOptions,
  enduser,
  session,
  form,
  formResponses,
  enduserId,
  organizationSubdomain,
  description,
  htmlDescription,
  applyDatabasePrepopulation,
  setCustomerId,
}: QuestionGroupFieldProps) => {
  const responsesArray = useMemo(() => Object.values(currentResponses), [currentResponses]);
  const subFieldDefs = options?.subFields ?? [];

  const bmiDisplay = useMemo(() => {
    const hAnswer =
      responsesArray.find((r) => r.answer.type === "number" && r.answer.value && r.computedValueKey === "Height")
        ?.answer ||
      responsesArray.find((r) => r.answer.type === "Height" && r.answer.value && r.computedValueKey === "Height")
        ?.answer;
    const wAnswer = responsesArray.find(
      (r) => r.answer.type === "number" && r.answer.value && r.computedValueKey === "Weight"
    )?.answer;
    const height =
      hAnswer?.type === "number" && hAnswer.value
        ? hAnswer.value
        : hAnswer?.type === "Height" && typeof hAnswer.value?.feet === "number"
        ? hAnswer.value.feet * 12 + (hAnswer.value.inches || 0)
        : undefined;
    const weight = wAnswer?.type === "number" && wAnswer.value ? wAnswer.value : undefined;
    if (!(height && weight)) return undefined;
    return ((703 * weight) / (height * height)).toFixed(1);
  }, [responsesArray]);
  const substituteBMI = useCallback(
    (text: string) => (bmiDisplay ? text.replace(/\{\{enduser\.BMI\}\}/g, bmiDisplay) : text),
    [bmiDisplay]
  );

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        {!!title && <h4 className="text-xl font-bold">{title}</h4>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
      </div>
      <div
        data-testid="question-group-sub-fields"
        className={cn("flex flex-col", options?.groupPadding === undefined && "gap-8")}
        style={options?.groupPadding === undefined ? undefined : { gap: `${options.groupPadding}px` }}
      >
        {subFieldDefs.map(({ id: subFieldId }) => {
          const subField = allFields.find((f) => f.id === subFieldId);
          if (!subField) return null;

          if (subField.groupShowCondition && Object.keys(subField.groupShowCondition).length) {
            const isVisible = responsesSatisfyConditions(responsesArray, subField.groupShowCondition, logicOptions);
            if (!isVisible) return null;
          }

          const convertedType = convertFormFieldType(subField.type);
          if (convertedType === "Question Group") return null;
          if (convertedType === null) {
            if (subField.type !== "description") return null;
            return (
              <div key={subFieldId} className="space-y-1">
                {!!subField.title && <p className="text-sm">{substituteBMI(subField.title)}</p>}
                <FieldDescription
                  description={substituteBMI(subField.description ?? "") || undefined}
                  htmlDescription={substituteBMI(subField.htmlDescription ?? "") || undefined}
                />
              </div>
            );
          }

          switch (convertedType) {
            case "Appointment Booking": {
              if (!session || !enduserId) return null;
              const props = getFieldProps(subFieldId, "Appointment Booking");
              if (!props) return null;
              return (
                <TellescopeFormField<"Appointment Booking">
                  key={subFieldId}
                  {...props}
                  session={session}
                  enduserId={enduserId}
                  organizationSubdomain={organizationSubdomain}
                />
              );
            }
            case "Stripe": {
              if (!form || !enduserId) return null;
              const props = getFieldProps(subFieldId, "Stripe");
              if (!props) return null;
              return (
                <TellescopeFormField<"Stripe">
                  key={subFieldId}
                  {...props}
                  enduserId={enduserId}
                  form={form}
                  formResponses={formResponses ?? responsesArray}
                  setCustomerId={setCustomerId}
                />
              );
            }
            case "Chargebee": {
              if (!session || !enduserId) return null;
              const props = getFieldProps(subFieldId, "Chargebee");
              if (!props) return null;
              return (
                <TellescopeFormField<"Chargebee">
                  key={subFieldId}
                  {...props}
                  session={session}
                  enduserId={enduserId}
                  formResponses={formResponses ?? responsesArray}
                  isOptional={subField.isOptional}
                  setCustomerId={setCustomerId}
                />
              );
            }
            case "Database Select": {
              const props = getFieldProps(subFieldId, "Database Select");
              if (!props) return null;
              return (
                <TellescopeFormField<"Database Select">
                  key={subFieldId}
                  {...props}
                  formResponses={formResponses ?? responsesArray}
                  enduser={enduser}
                  onDatabaseSelect={applyDatabasePrepopulation}
                />
              );
            }
            case "Insurance": {
              const props = getFieldProps(subFieldId, "Insurance");
              if (!props) return null;
              return (
                <TellescopeFormField<"Insurance">
                  key={subFieldId}
                  {...props}
                  formResponses={formResponses ?? responsesArray}
                  enduser={enduser}
                  onDatabaseSelect={applyDatabasePrepopulation}
                />
              );
            }
            case "signature": {
              const props = getFieldProps(subFieldId, "signature");
              if (!props) return null;
              return (
                <TellescopeFormField<"signature">
                  key={subFieldId}
                  {...props}
                  fullName={enduser ? formatName(enduser) : ""}
                />
              );
            }
            case "multiple_choice": {
              const props = getFieldProps(subFieldId, "multiple_choice");
              if (!props) return null;
              return (
                <TellescopeFormField<"multiple_choice">
                  key={subFieldId}
                  {...props}
                  checkboxList
                  formResponses={formResponses ?? responsesArray}
                  logicOptions={logicOptions}
                />
              );
            }
            default: {
              const limitedType: Exclude<FormTypesWithValues, "Appointment Booking" | "Stripe" | "Chargebee"> =
                convertedType;
              const props = getFieldProps<typeof limitedType>(subFieldId, limitedType);
              if (!props) return null;
              // @ts-expect-error Union type has been reduced, and TS doesn't like it
              return <TellescopeFormField<typeof limitedType> key={subFieldId} {...props} />;
            }
          }
        })}
      </div>
    </div>
  );
};
